import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ContentCard from '@/components/feature/ContentCard';
import { SectionHeader } from '@/components/feature/SectionHeader';
import SchemaOrg from '@/components/base/SchemaOrg';
import { ARTICLE_DATA, RELATED_ARTICLES } from '@/mocks';
import NotFound from '@/pages/NotFound';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? ARTICLE_DATA[slug] : undefined;
  const related = slug ? RELATED_ARTICLES[slug] || [] : [];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!article) {
    return <NotFound />;
  }

  return (
    <>
      <SchemaOrg type="Article" data={{
        headline: article.title,
        description: article.description,
        image: article.image,
        datePublished: article.publishDate,
        author: { '@type': 'Organization', name: '777影院' },
        publisher: { '@type': 'Organization', name: '777影院' },
      }} />

      <div className="min-h-screen bg-background-950">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.category, href: `/category/${article.categorySlug}` },
              { label: article.title },
            ]}
          />

          <article className="px-4 md:px-6 py-4">
            <div className="max-w-[1200px] mx-auto">
              <div className="flex flex-col lg:flex-row gap-8">
                <div className="lg:w-[300px] shrink-0">
                  <div className="sticky top-24">
                    <div className="relative aspect-[2/3] rounded-lg overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        title={`${article.title} - 777影院在线观看`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="mt-4 p-4 rounded-lg bg-background-800/50">
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-foreground-500">评分</span>
                          <span className="text-accent-500 font-semibold flex items-center gap-1">
                            <i className="ri-star-fill text-xs"></i>{article.rating}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-foreground-500">年份</span>
                          <span className="text-foreground-300">{article.year}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-foreground-500">导演</span>
                          <span className="text-foreground-300">{article.director}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-foreground-500">片长</span>
                          <span className="text-foreground-300">{article.duration}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-foreground-500">状态</span>
                          <span className="text-accent-500">{article.updateInfo}</span>
                        </div>
                      </div>
                      <button className="w-full mt-4 px-6 py-2.5 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 font-medium text-sm transition-colors whitespace-nowrap flex items-center justify-center gap-2">
                        <i className="ri-play-circle-fill text-lg"></i>
                        立即观看
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-100 mb-2">
                    {article.title}
                  </h1>
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <span className="px-2 py-0.5 rounded text-xs bg-background-800 text-foreground-400">{article.category}</span>
                    <span className="text-sm text-foreground-500">{article.publishDate}</span>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {article.cast.map((actor) => (
                      <span key={actor} className="px-3 py-1 rounded-full text-xs bg-background-800 text-foreground-400">{actor}</span>
                    ))}
                  </div>
                  <p className="text-sm md:text-base text-foreground-400 leading-relaxed mb-6">
                    {article.description}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-8">
                    {article.tags.map((tag) => (
                      <span key={tag} className="px-3 py-1 rounded-full text-xs bg-secondary-500/10 text-secondary-500 border border-secondary-500/20">
                        #{tag}
                      </span>
                    ))}
                  </div>

                  <div className="prose prose-invert max-w-none">
                    {article.content.split('\n').map((paragraph, i) => (
                      <p key={i} className="text-sm md:text-base text-foreground-400 leading-relaxed mb-4">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </article>

          {related.length > 0 && (
            <section className="px-4 md:px-6 pt-8 pb-14 md:pb-20" aria-labelledby="related-heading">
              <div className="max-w-[1200px] mx-auto">
                <SectionHeader title="相关推荐" subtitle="你可能也会喜欢" />
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
                  {related.map((item) => (
                    <ContentCard key={item.id} item={item} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}